import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import HomeNavbar from "../components/candidate/HomeNavbar";
import { EventsApi } from "../utils/eventsApi";
import { ResourcesApi } from "../utils/resourcesApi";

const RESOURCE_TYPES = [
  { value: "", label: "All types" },
  { value: "article", label: "Article" },
  { value: "video", label: "Video" },
  { value: "podcast", label: "Podcast" },
  { value: "ebook", label: "E-Book" },
  { value: "template", label: "Template" },
  { value: "tool", label: "Tool" },
  { value: "course", label: "Course" },
  { value: "guide", label: "Guide" },
  { value: "other", label: "Other" },
];

export default function ResourcesBrowse() {
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [resources, setResources] = useState([]);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [resourceType, setResourceType] = useState("");
  const [freeOnly, setFreeOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    ResourcesApi.list({
      search,
      resource_type: resourceType,
      is_free: freeOnly ? "true" : undefined,
    })
      .then((data) => {
        setResources(data.results || []);
        setCount(data.count || 0);
        return undefined;
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const timeout = setTimeout(load, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, resourceType, freeOnly]);

  useEffect(() => {
    EventsApi.upcoming()
      .then((data) => {
        setEvents(Array.isArray(data) ? data : []);
        return undefined;
      })
      .catch(() => undefined)
      .finally(() => setEventsLoading(false));
  }, []);

  return (
    <>
      <HomeNavbar />
      <div className="mt-16 min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="mx-auto max-w-5xl">
          <h1 className="text-3xl font-semibold text-gray-800 dark:text-gray-100">
            Career Resources
          </h1>
          <p className="mt-1 mb-6 text-gray-600 dark:text-gray-400">
            Events, articles, courses, tools, and guides to help you grow.
          </p>

          <section className="mb-8">
            <h2 className="mb-3 text-lg font-semibold text-gray-800 dark:text-gray-100">
              Upcoming events
            </h2>
            {eventsLoading ? (
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Loading events...
              </p>
            ) : events.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500">
                No upcoming events in the next 30 days.
              </p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {events.map((e) => (
                  <EventCard key={e.id} event={e} />
                ))}
              </div>
            )}
          </section>

          <h2 className="mb-3 text-lg font-semibold text-gray-800 dark:text-gray-100">
            Resources
          </h2>
          <div className="mb-6 flex flex-col gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm sm:flex-row sm:items-center">
            <input
              className="flex-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              placeholder="Search resources..."
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              aria-label="Resource type"
              className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              value={resourceType}
              onChange={(e) => setResourceType(e.target.value)}
            >
              {RESOURCE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <label
              className="flex items-center gap-2 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400"
              htmlFor="free-only-checkbox"
            >
              <input
                checked={freeOnly}
                id="free-only-checkbox"
                type="checkbox"
                onChange={(e) => setFreeOnly(e.target.checked)}
              />
              Free only
            </label>
          </div>

          {loading ? (
            <p className="py-12 text-center text-gray-400 dark:text-gray-500">
              Loading...
            </p>
          ) : resources.length === 0 ? (
            <p className="py-12 text-center text-gray-400 dark:text-gray-500">
              No resources match your filters.
            </p>
          ) : (
            <>
              <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                {count} resources
              </p>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {resources.map((r) => (
                  <ResourceCard key={r.id} resource={r} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function EventCard({ event }) {
  const date = new Date(event.start_date);
  const formatted = Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
  return (
    <div className="flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <span className="rounded-full bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 text-xs font-semibold capitalize text-blue-700 dark:text-blue-300">
          {event.event_type}
        </span>
        {event.is_online ? (
          <span className="rounded-full bg-green-100 dark:bg-green-900/40 px-2 py-0.5 text-xs font-semibold text-green-700 dark:text-green-300">
            Online
          </span>
        ) : null}
      </div>
      <h3 className="font-semibold text-gray-800 dark:text-gray-100">
        {event.title}
      </h3>
      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        {formatted}
        {event.city ? ` · ${event.city}` : ""}
      </p>
      {event.tags && event.tags.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {event.tags.map((tag) => (
            <span
              key={tag.id}
              className="rounded bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-400"
            >
              {tag.name}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

EventCard.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    title: PropTypes.string.isRequired,
    event_type: PropTypes.string,
    start_date: PropTypes.string,
    city: PropTypes.string,
    is_online: PropTypes.bool,
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        name: PropTypes.string,
      }),
    ),
  }).isRequired,
};

function ResourceCard({ resource }) {
  return (
    <div className="flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <span className="rounded-full bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 text-xs font-semibold capitalize text-blue-700 dark:text-blue-300">
          {resource.resource_type}
        </span>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
            resource.is_free
              ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300"
              : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
          }`}
        >
          {resource.is_free ? "Free" : "Paid"}
        </span>
      </div>
      <h3 className="font-semibold text-gray-800 dark:text-gray-100">
        {resource.title}
      </h3>
      {resource.author ? (
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          by {resource.author}
        </p>
      ) : null}
      {resource.tags && resource.tags.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {resource.tags.map((tag) => (
            <span
              key={tag.id}
              className="rounded bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-400"
            >
              {tag.name}
            </span>
          ))}
        </div>
      ) : null}
      <div className="mt-auto pt-3 text-xs text-gray-400 dark:text-gray-500">
        {resource.views_count} views
      </div>
    </div>
  );
}

ResourceCard.propTypes = {
  resource: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    title: PropTypes.string.isRequired,
    resource_type: PropTypes.string,
    author: PropTypes.string,
    is_free: PropTypes.bool,
    views_count: PropTypes.number,
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        name: PropTypes.string,
      }),
    ),
  }).isRequired,
};
